/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { Replay } from "./replay.js";
import { RunObserver } from "./run.js";
export class PastRun {
    dataStoreKey;
    #replay;
    #backingRun = null;
    edges = [];
    constructor(dataStoreKey = crypto.randomUUID(), graphStore, timeline) {
        this.dataStoreKey = dataStoreKey;
        this.#replay = new Replay(graphStore, timeline, 0);
    }
    async initializeBackingRun(graphStore) {
        const observer = new RunObserver(graphStore, {
            logLevel: "debug",
        });
        for await (const result of this.replay()) {
            await observer.observe(result);
        }
        this.#backingRun = (await observer.runs())[0];
    }
    get graphVersion() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide graph versions");
        }
        return this.#backingRun.graphVersion;
    }
    get start() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide start times");
        }
        return this.#backingRun.start;
    }
    get end() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide end times");
        }
        return this.#backingRun.end;
    }
    get events() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide events");
        }
        return this.#backingRun.events;
    }
    currentNodeEvent() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide current node events");
        }
        return this.#backingRun.currentNodeEvent();
    }
    stack() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide stack traces");
        }
        return this.#backingRun.stack();
    }
    getEventById(id) {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide event IDs");
        }
        return this.#backingRun.getEventById(id);
    }
    inputs() {
        if (!this.#backingRun) {
            throw new Error("Uninitialized run: can't yet provide inputs");
        }
        return this.#backingRun.inputs();
    }
    async *replay() {
        yield* this.#replay.replay();
    }
}
//# sourceMappingURL=past-run.js.map